import { Box, Stack, styled, Tab, Tabs, Tooltip } from "@mui/material";
import { API } from "aws-amplify";
import { useCreateApiCall } from "hooks/useCreateApiCall";
import { useDataGrid } from "hooks/useDataGrid";
import { useRegions } from "hooks/useRegions";
import { useTabs } from "hooks/useTabs";
import { useCurrentUserData } from "hooks/useUserData";
import { ReactComponent as EditIcon } from "images/edit-big-icon.svg";
import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as CSC from "SC";
import { GlobalContext } from "store/globalContext";
import { CommonOutletTemplate } from "UI/CommonOutletTemplate";
import { DataGrid } from "UI/DataGrid";
import { AddNewButtonLocationFlow } from "./AddNewButtonLocationFlow";
import { AddNewOriginatorDialog } from "./AddNewOriginatorDialog";
import { EditButtonLocationDialog } from "./EditButtonLocationDialog";
import { EditButtonLocationSuperAdminDialog } from "./EditButtonLocationSuperAdminDialog";
import { EditPracticeDialog } from "./EditPracticeDialog";
import { EditSpecialistDialog } from "./EditSpecialistDialog";
import { ModalSpinner } from "./ModalSpinner";
import * as SC from "./PracticeDetails.style";
import {
  buttonColumns,
  mobileButtonColumns,
} from "./PracticeDetailsButtons.config";
import { columns, mobileColumns } from "./PracticeDetailsOriginators.config";
import {
  referralsColumns,
  mobileReferralsColumns,
} from "./PracticeDetailsReferrals.config";
import {
  questionsColumns,
  questionsMobileColumns,
} from "./PracticeDetailsQuestions.config";
import {
  practiceNotificationsColumns,
  practiceNotificationsMobileColumns,
} from "./PracticeDetailsNotifications.config";
import {
  archiveColumns,
  archiveMobileColumns,
} from "./PracticeDetailsArchive.config";
import { TabPanel } from "./TabPanel";
import {
  providersColumns,
  providersMobileColumns,
} from "./PracticeDetailsProviders.config";
import { EditProviderDialog } from "./EditProviderDialog";
import { AddNewProviderDialog } from "./AddNewProviderDialog";

const EditBigIcon = styled(EditIcon)`
  cursor: pointer;
`;

export const PracticeDetails = () => {
  const navigate = useNavigate();
  const { createApiCall } = useCreateApiCall();
  const { regionsOptions, refreshRegionsOptions, getRegionById } = useRegions();
  const { currentTab, handleChangeTab } = useTabs({
    key: "PracticeDetails",
  });
  const { userData } = useContext(GlobalContext);

  const { id } = useParams();
  const [data, setData] = useState();

  const [isEditPracticeOpen, setIsEditPracticeOpen] = useState(false);
  const [isAddNewDialogOpen, setIsAddNewDialogOpen] = useState(false);
  const [currentRole, setCurrentRole] = useState("");
  const [currentSpecialistId, setCurrentSpecialistId] = useState("");
  const [currentButtonId, setCurrentButtonId] = useState("");
  const [currentProviderId, setCurrentProviderId] = useState("");

  const { isReadOnlyAccess, isSuperAdmin, isScheduler } = useCurrentUserData();

  const [isAddNewButtonDialogOpen, setIsAddNewButtonDialogOpen] =
    useState(false);
  const [isAddNewProviderDialogOpen, setIsAddNewProviderDialogOpen] =
    useState(false);

  const openAddSenderDialog = () => {
    setCurrentRole("Originator");
    setIsAddNewDialogOpen(true);
  };

  const {
    name,
    speciality,
    phone,
    street,
    street2,
    city,
    state,
    zipCode,
    website,
    email,
    marketingRegionId,
  } =
    // @ts-ignore
    data?.Item ?? {};

  const handleRowClick = ({ id }) => setCurrentSpecialistId(id);

  const handleProviderRowClick = ({ id }) => setCurrentProviderId(id);

  const providersDataGrid = useDataGrid({
    url: "/providers",
    defaultSorter: {
      name: "name",
      order: "asc",
    },
    defaultLimit: 25,
    gridConfigIndex: `${userData.cognitoId}-Practice-Providers`,
    additionalParams: useMemo(
      () => ({
        organizationId: id,
      }),
      [id]
    ),
  });

  const sendersDataGrid = useDataGrid({
    url: "/originators",
    defaultSorter: {
      name: "name",
      order: "asc",
    },
    defaultLimit: 25,
    gridConfigIndex: `${userData.cognitoId}-Practice-Senders`,
    additionalParams: useMemo(
      () => ({
        organizationId: id,
        role: "Originator",
      }),
      [id]
    ),
  });

  const buttonsDataGrid = useDataGrid({
    url: "/all_button_locations",
    defaultSorter: {
      name: "practiceName",
      order: "asc",
    },
    defaultLimit: 25,
    gridConfigIndex: `${userData.cognitoId}-Practice-Buttons`,
    additionalParams: useMemo(
      () => ({
        organizationId: id,
      }),
      [id]
    ),
  });

  const handleButtonRowClick = ({ id }) => {
    isReadOnlyAccess
      ? navigate(`/button_location/${id}`)
      : setCurrentButtonId(id);
  };

  const notificationsDataGrid = useDataGrid({
    url: "/referrals",
    defaultSorter: {
      name: "createdAt",
      order: "desc",
    },
    defaultLimit: 25,
    gridConfigIndex: `${userData.cognitoId}-Practice-Notifications`,
    additionalParams: useMemo(
      () => ({
        practiceId: id,
        type: "Notification",
      }),
      [id]
    ),
  });

  const questionsDataGrid = useDataGrid({
    url: "/referrals",
    defaultSorter: {
      name: "createdAt",
      order: "desc",
    },
    defaultLimit: 25,
    gridConfigIndex: `${userData.cognitoId}-Practice-Questions`,
    additionalParams: useMemo(
      () => ({
        practiceId: id,
        type: "Question",
      }),
      [id]
    ),
  });

  const referralsDataGrid = useDataGrid({
    url: "/referrals",
    defaultSorter: {
      name: "createdAt",
      order: "desc",
    },
    defaultLimit: 25,
    gridConfigIndex: `${userData.cognitoId}-Practice-Referrals`,
    additionalParams: useMemo(
      () => ({
        practiceId: id,
        type: "Referral",
      }),
      [id]
    ),
  });

  const archiveDataGrid = useDataGrid({
    url: "/referrals",
    defaultSorter: {
      name: "createdAt",
      order: "desc",
    },
    defaultLimit: 25,
    gridConfigIndex: `${userData.cognitoId}-Practice-Archive`,
    additionalParams: useMemo(
      () => ({
        practiceId: id,
        type: "Archive",
      }),
      [id]
    ),
  });

  const handleReferralRowClick = ({ id }) => {
    navigate(`/referral/${id}`);
  };

  const getData = createApiCall(async () => {
    // load recipient details by recipient id
    const recipients = await API.get("restApi", `/recipients/${id}`, {});
    setData(recipients);
  });

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    switch (currentTab) {
      case 0:
        providersDataGrid.loadData();
        break;
      case 1:
        sendersDataGrid.loadData();
        break;
      case 2:
        buttonsDataGrid.loadData();
        break;
      case 3:
        notificationsDataGrid.loadData();
        break;
      case 4:
        questionsDataGrid.loadData();
        break;
      case 5:
        referralsDataGrid.loadData();
        break;
      case 6:
        archiveDataGrid.loadData();
        break;
      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab]);

  const refreshList = sendersDataGrid.loadData;

  const editDialogTitle = "Sender Details";

  return (
    <CommonOutletTemplate title={"Practice Details"} backIcon>
      {data ? (
        <div className="flex-grow flex-shrink-0 sm:flex-shrink flex flex-col gap-4 p-0 overflow-hidden">
          <div className="flex flex-row items-center gap-4">
            {/* <div className="block lg:hidden">
              <BackToPrevPage isMobile backLink={"/practices"} />
            </div> */}
            <Stack
              mt={2}
              flex={1}
              direction={"row"}
              justifyContent={"flex-start"}
              alignItems={"center"}
              gap={2}
            >
              <SC.Title>{name}</SC.Title>
              <Tooltip title="Edit practice details">
                <EditBigIcon onClick={() => setIsEditPracticeOpen(true)} />
              </Tooltip>
            </Stack>
          </div>
          <div className="bg-white border border-solid border-gray-300 rounded-2xl p-4 md:p-6">
            <SC.InfoSection>
              {speciality ? (
                <SC.Info>
                  <SC.Label>Speciality:</SC.Label> {speciality}
                </SC.Info>
              ) : (
                ""
              )}
              <SC.Info>
                <SC.Label>Address:</SC.Label>{" "}
                {street2 ? `${street}, ${street2}` : street}, {city}, {state},{" "}
                {zipCode}
              </SC.Info>
              {!!phone && (
                <SC.Info>
                  <SC.Label>Phone:</SC.Label>{" "}
                  <a href={`tel:${phone}`}>{phone}</a>
                </SC.Info>
              )}
              {!!email && (
                <SC.Info>
                  <SC.Label>Email:</SC.Label>{" "}
                  <a href={`mailto:${email}`}>{email}</a>
                </SC.Info>
              )}
              {!!website && (
                <SC.Info>
                  <SC.Label>Website:</SC.Label>{" "}
                  <a href={website} className="break-all">
                    {website}
                  </a>
                </SC.Info>
              )}
              {!!marketingRegionId &&
                !!regionsOptions &&
                marketingRegionId in regionsOptions && (
                  <SC.Info>
                    <SC.Label>Marketing region:</SC.Label>{" "}
                    {getRegionById(marketingRegionId)}
                  </SC.Info>
                )}
            </SC.InfoSection>
          </div>
          <div
            className={`
              flex-1 bg-white border border-solid border-gray-300 rounded-2xl p-4 md:p-6 pt-2 md:pt-2 overflow-hidden
              flex flex-col justify-between items-stretch
            `}
          >
            <Box sx={{ flex: 0, borderBottom: 1, borderColor: "divider" }}>
              <Tabs
                value={currentTab}
                onChange={handleChangeTab}
                aria-label="basic tabs example"
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
              >
                <Tab label="Providers" />
                <Tab label="Senders" />
                <Tab label="Buttons" />
                <Tab label="Notifications" />
                <Tab label="Questions" />
                <Tab label="Referrals" />
                <Tab label="Archive" />
              </Tabs>
            </Box>
            <div className="flex-1 flex flex-col justify-between items-stretch mt-4 overflow-hidden">
              <TabPanel value={currentTab} index={0}>
                <DataGrid
                  columns={providersColumns}
                  mobileColumns={providersMobileColumns}
                  handleRowClick={handleProviderRowClick}
                  {...providersDataGrid}
                  afterSearchContent={
                    <CSC.BlueButton
                      variant="outlined"
                      onClick={() => setIsAddNewProviderDialogOpen(true)}
                    >
                      Add New Provider
                    </CSC.BlueButton>
                  }
                  withRefreshButton
                />
              </TabPanel>
              <TabPanel value={currentTab} index={1}>
                <DataGrid
                  columns={columns}
                  mobileColumns={mobileColumns}
                  handleRowClick={handleRowClick}
                  {...sendersDataGrid}
                  afterSearchContent={
                    <CSC.BlueButton
                      variant="outlined"
                      onClick={openAddSenderDialog}
                    >
                      Add New Sender
                    </CSC.BlueButton>
                  }
                  withRefreshButton
                />
              </TabPanel>
              <TabPanel value={currentTab} index={2}>
                <DataGrid
                  columns={buttonColumns}
                  mobileColumns={mobileButtonColumns}
                  handleRowClick={handleButtonRowClick}
                  {...buttonsDataGrid}
                  afterSearchContent={
                    !isReadOnlyAccess &&
                    !isScheduler &&
                    !isSuperAdmin && (
                      <CSC.BlueButton
                        variant="outlined"
                        onClick={() => setIsAddNewButtonDialogOpen(true)}
                      >
                        Add New Button
                      </CSC.BlueButton>
                    )
                  }
                  withRefreshButton
                />
              </TabPanel>
              <TabPanel value={currentTab} index={3}>
                <DataGrid
                  columns={practiceNotificationsColumns}
                  mobileColumns={practiceNotificationsMobileColumns}
                  handleRowClick={handleReferralRowClick}
                  {...notificationsDataGrid}
                  withRefreshButton
                />
              </TabPanel>
              <TabPanel value={currentTab} index={4}>
                <DataGrid
                  columns={questionsColumns}
                  mobileColumns={questionsMobileColumns}
                  handleRowClick={handleReferralRowClick}
                  {...questionsDataGrid}
                  withRefreshButton
                />
              </TabPanel>
              <TabPanel value={currentTab} index={5}>
                <DataGrid
                  columns={referralsColumns}
                  mobileColumns={mobileReferralsColumns}
                  handleRowClick={handleReferralRowClick}
                  {...referralsDataGrid}
                  withRefreshButton
                />
              </TabPanel>
              <TabPanel value={currentTab} index={6}>
                <DataGrid
                  columns={archiveColumns}
                  mobileColumns={archiveMobileColumns}
                  handleRowClick={handleReferralRowClick}
                  {...archiveDataGrid}
                  withRefreshButton
                />
              </TabPanel>
            </div>
          </div>
        </div>
      ) : (
        <ModalSpinner open />
      )}
      <AddNewOriginatorDialog
        open={isAddNewDialogOpen}
        handleClose={() => setIsAddNewDialogOpen(false)}
        refreshList={refreshList}
        organizationId={id}
        setResultValue={() => null}
        role={currentRole}
      />
      <EditSpecialistDialog
        title={editDialogTitle}
        role={currentRole}
        specialistId={currentSpecialistId}
        handleClose={() => setCurrentSpecialistId("")}
        refreshList={refreshList}
      />
      <EditPracticeDialog
        open={isEditPracticeOpen}
        practiceId={id}
        handleClose={() => setIsEditPracticeOpen(false)}
        refreshList={() => getData()}
        regionsOptions={regionsOptions}
        refreshRegionsOptions={refreshRegionsOptions}
      />
      <EditProviderDialog
        open={!!currentProviderId}
        practiceId={id}
        providerId={currentProviderId}
        handleClose={() => setCurrentProviderId("")}
        refreshList={providersDataGrid.loadData}
      />
      <AddNewProviderDialog
        open={isAddNewProviderDialogOpen}
        handleClose={() => setIsAddNewProviderDialogOpen(false)}
        refreshList={providersDataGrid.loadData}
        organizationId={id}
        setResultValue={() => null}
      />
      {!isReadOnlyAccess && isAddNewButtonDialogOpen && (
        <AddNewButtonLocationFlow
          handleClose={() => setIsAddNewButtonDialogOpen(false)}
          refreshList={() => buttonsDataGrid.loadData()}
          setCurrentButtonId={setCurrentButtonId}
          selectedPractice={{ id, name }}
        />
      )}
      {currentButtonId && userData?.role === "Super Admin" && (
        <EditButtonLocationSuperAdminDialog
          buttonId={currentButtonId}
          handleClose={() => setCurrentButtonId("")}
          refreshList={() => buttonsDataGrid.loadData()}
        />
      )}
      {currentButtonId && ["Admin", "Marketer"].includes(userData?.role) && (
        <EditButtonLocationDialog
          buttonId={currentButtonId}
          handleClose={() => setCurrentButtonId("")}
          refreshList={() => buttonsDataGrid.loadData()}
          practiceId={id}
        />
      )}
    </CommonOutletTemplate>
  );
};
